/** Matches @local@domain.tld tokens embedded in message text. */
const MENTION_REGEX = /@([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;

export interface EmailMentionMatch {
  start: number;
  end: number;
  email: string;
  token: string;
}

function isMentionTriggerAt(text: string, index: number): boolean {
  if (text[index] !== '@') return false;
  return index === 0 || /\s/.test(text[index - 1]);
}

function isCompleteMentionSegment(segment: string): boolean {
  return new RegExp(MENTION_REGEX.source).test(segment);
}

/** `@` was not followed by a mention token (e.g. "@ " or "@ see also"). */
function isAbandonedMentionSegment(segment: string): boolean {
  return /^@\s/.test(segment);
}

/**
 * Index of the rightmost mention-start `@` (start of text or after whitespace)
 * whose segment is not yet a complete @email@domain token.
 */
export function getMentionTriggerIndex(text: string): number | null {
  if (!text) return null;
  let candidate: number | null = null;
  for (let i = 0; i < text.length; i++) {
    if (!isMentionTriggerAt(text, i)) continue;
    const segment = text.slice(i);
    if (isAbandonedMentionSegment(segment)) continue;
    if (isCompleteMentionSegment(segment)) continue;
    candidate = i;
  }
  return candidate;
}

/** Hint copy while the user is composing an @email@domain mention. */
export function getMentionComposeHint(text: string): string | null {
  const start = getMentionTriggerIndex(text);
  if (start === null) return null;
  const segment = text.slice(start);
  if (segment.indexOf('@', 1) >= 0) {
    return 'Choose someone from the list or finish the mention token.';
  }
  return 'Type @ then pick someone from the list (only people allowed on this file location).';
}

/** @deprecated Prefer getMentionComposeHint; true when a compose hint should show. */
export function shouldShowMentionHint(text: string): boolean {
  return getMentionComposeHint(text) !== null;
}

/** True when text has an unfinished @email@domain mention token. */
export function hasIncompleteMention(text: string): boolean {
  return getMentionTriggerIndex(text) !== null;
}

export function findMentions(text: string, max = 10): EmailMentionMatch[] {
  if (!text) return [];
  const seen = new Set<string>();
  const list: EmailMentionMatch[] = [];
  const re = new RegExp(MENTION_REGEX.source, 'g');
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const email = match[1].toLowerCase();
    if (seen.has(email)) continue;
    seen.add(email);
    list.push({
      start: match.index,
      end: match.index + match[0].length,
      email,
      token: match[0]
    });
    if (list.length >= max) break;
  }
  return list;
}

export type MentionTextPart = { type: 'text' | 'mention'; value: string };

export function splitTextWithMentions(text: string): MentionTextPart[] {
  const mentions = findMentions(text);
  if (mentions.length === 0) return [{ type: 'text', value: text }];
  const parts: MentionTextPart[] = [];
  let cursor = 0;
  for (const m of mentions) {
    if (m.start > cursor) parts.push({ type: 'text', value: text.slice(cursor, m.start) });
    parts.push({ type: 'mention', value: m.token });
    cursor = m.end;
  }
  if (cursor < text.length) parts.push({ type: 'text', value: text.slice(cursor) });
  return parts;
}
